import { useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { parseQrPayload, type QrScan } from '@inventohub/shared';
import jsQR from 'jsqr';

interface BarcodeHit {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect: (source: ImageBitmapSource) => Promise<BarcodeHit[]>;
}

function createBarcodeDetector(): BarcodeDetectorLike | null {
  const ctor = (
    window as unknown as {
      BarcodeDetector?: new (opts: { formats: string[] }) => BarcodeDetectorLike;
    }
  ).BarcodeDetector;
  if (!ctor) return null;
  try {
    return new ctor({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

export function QrScanPanel({
  label,
  value,
  onValueChange,
  onScan,
  submitLabel,
  inputTestId,
  disabled = false,
  cameraOnly = false,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onScan: (scan: QrScan) => void;
  submitLabel: string;
  inputTestId: string;
  disabled?: boolean;
  /** Hub / search Scan QR overlay: camera only; typed lookup lives in header search. */
  cameraOnly?: boolean;
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onScanRef = useRef(onScan);
  const onValueChangeRef = useRef(onValueChange);
  const [scanning, setScanning] = useState(cameraOnly);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    onScanRef.current = onScan;
    onValueChangeRef.current = onValueChange;
  }, [onScan, onValueChange]);

  useEffect(() => {
    if (!scanning) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    let cancelled = false;
    let stream: MediaStream | null = null;
    let raf = 0;
    let detecting = false;
    const detector = createBarcodeDetector();

    function applyRaw(raw: string): void {
      const parsed = parseQrPayload(raw);
      if (!parsed) return;
      if (parsed.kind === 'tote') onValueChangeRef.current(parsed.number);
      else if (parsed.kind === 'asset') onValueChangeRef.current(parsed.serial);
      else if (parsed.kind === 'slug') onValueChangeRef.current(parsed.slug);
      else onValueChangeRef.current(parsed.value);
      onScanRef.current(parsed);
      setScanning(false);
    }

    function tick(preview: HTMLVideoElement, frame: HTMLCanvasElement): void {
      if (cancelled) return;
      if (preview.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        if (detector) {
          if (!detecting) {
            detecting = true;
            void detector
              .detect(preview)
              .then((codes) => {
                const hit = codes[0]?.rawValue;
                if (hit) applyRaw(hit);
              })
              .catch(() => undefined)
              .finally(() => {
                detecting = false;
              });
          }
        } else {
          const context = frame.getContext('2d', { willReadFrequently: true });
          if (context) {
            frame.width = preview.videoWidth || 640;
            frame.height = preview.videoHeight || 480;
            context.drawImage(preview, 0, 0, frame.width, frame.height);
            const pixels = context.getImageData(0, 0, frame.width, frame.height);
            const code = jsQR(pixels.data, pixels.width, pixels.height, {
              inversionAttempts: 'attemptBoth',
            });
            if (code?.data) applyRaw(code.data);
          }
        }
      }
      raf = window.requestAnimationFrame(() => tick(preview, frame));
    }

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        video.srcObject = stream;
        await video.play();
        raf = window.requestAnimationFrame(() => tick(video, canvas));
      } catch {
        if (!cancelled) {
          setCameraError(
            cameraOnly
              ? 'Camera is unavailable. Type the tote number or serial in search instead.'
              : 'Camera is unavailable. Type the tote number or serial instead.',
          );
          setScanning(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      stream?.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    };
  }, [scanning, cameraOnly]);

  function onTyped(event: FormEvent): void {
    event.preventDefault();
    const parsed = parseQrPayload(value);
    if (parsed) onScan(parsed);
  }

  return (
    <div className="qr-scan" data-testid="qr-scan">
      {cameraOnly ? null : (
        <form onSubmit={onTyped}>
          <label className="field">
            {label}
            <input
              value={value}
              onChange={(event) => onValueChange(event.target.value)}
              required
              disabled={disabled}
              data-testid={inputTestId}
              autoComplete="off"
              inputMode="text"
            />
          </label>
          <button type="submit" className="button button--ghost" disabled={disabled}>
            {submitLabel}
          </button>
        </form>
      )}
      <button
        type="button"
        className="button"
        disabled={disabled}
        data-testid="qr-scan-camera"
        onClick={() => {
          setCameraError(null);
          setScanning((open) => !open);
        }}
      >
        {scanning ? 'Stop camera' : 'Scan QR with camera'}
      </button>
      {cameraError ? (
        <p className="status status--warn" data-testid="qr-scan-camera-error">
          {cameraError}
        </p>
      ) : null}
      {scanning ? (
        <div className="qr-scan__preview">
          <video
            ref={videoRef}
            className="qr-video"
            autoPlay
            muted
            playsInline
            data-testid="qr-scan-video"
          />
          <canvas ref={canvasRef} className="qr-canvas" aria-hidden="true" />
          <p className="meta">
            Point the camera at a tote or asset QR. You can still type instead.
          </p>
        </div>
      ) : null}
    </div>
  );
}
